import { type ApiClient, ApiError, createApiClient } from '@repo/api-client';
import { createContext, useContext } from 'react';

/**
 * The back-office's one API client, and how a screen gets hold of it.
 *
 * It is a context rather than a module singleton so a test can hand a screen a
 * client built over a stub `fetch`, and so the session credential is added in
 * one place without every screen learning about it.
 */

/**
 * What this module needs from the session: a token to send, and a way to end it.
 *
 * Narrower than `SessionHolder` on purpose — the client neither renders a
 * session nor decides what one is, and stating the two methods it does use
 * keeps the dependency pointing one way.
 */
export interface SessionCredential {
  /** The bearer token, or `undefined` when nobody is signed in. */
  token(): string | undefined;

  /**
   * End the session locally. Called when the API says it is already over.
   *
   * The reason is spelled out rather than defaulted, and this module only ever
   * passes one of the two: from here a session can only end by having expired.
   * A sign-out is the other caller and it is not this one.
   */
  clear(reason: 'expired'): void;
}

/**
 * Build the client this app talks to the API with.
 *
 * `baseUrl` is never defaulted — a client pointed at a literal is a client that
 * ships talking to somebody's laptop. Vite inlines `VITE_API_URL` at build time,
 * so a missing one is a misbuilt bundle and must say so at startup rather than
 * fail as a request to `undefined/v1/auth/sign-in` on the first sign-in.
 */
export function createWebApiClient(session: SessionCredential): ApiClient {
  const baseUrl = import.meta.env.VITE_API_URL;

  if (!baseUrl) {
    throw new Error('VITE_API_URL is not set. The back-office has no API to talk to.');
  }

  const client = createApiClient({
    baseUrl,

    credentials: {
      // Resolved per request, so the session issued by a sign-in that happened
      // after this client was built is picked up without rebuilding it. Which
      // operations receive it is not decided here: the transport reads each
      // operation's own `security`, so the three public ones — sign-in among
      // them — are sent no credential at all.
      //
      // No `terminalLease` provider. This app is the browser back-office; it
      // has no device lease to offer, and a provider that returned something
      // would be sending a person's credential where a device's belongs.
      session: () => session.token(),
    },
  });

  return endTheSessionWhenTheApiSaysItIsOver(client, session);
}

/**
 * One place where `AUTH_SESSION_EXPIRED` ends the session — for every request,
 * from every screen, including ones that have not been written yet.
 *
 * A session can lapse between two keystrokes, so the code is not a special case
 * belonging to whichever screen happened to be fetching when it arrived. Left to
 * each caller it would be handled in most of them, worded eighteen ways, and
 * missed in the one place that mattered — and the shape of the miss is a toast
 * on a screen the user can no longer use.
 *
 * This wraps the transport from outside rather than being an interceptor inside
 * `packages/api-client`. That package is the generated contract with the API and
 * has no business knowing that this app keeps a session or what it does about
 * one; the terminals consume the same client and answer this differently.
 *
 * The error is re-thrown. The caller's request still failed and it still has a
 * spinner to stop; what it must not do is *word* the failure, because by then
 * the redirect to sign-in is already on its way.
 */
function endTheSessionWhenTheApiSaysItIsOver(
  client: ApiClient,
  session: SessionCredential,
): ApiClient {
  /**
   * Both calls end the same way, so the decision lives once.
   *
   * A CSV export is as capable of outliving a session as any read — it is the
   * slowest thing in the back office and the one most likely to be started and
   * left — so `download` gets this too. Wrapping only `request` would have made
   * an expired session on an export the one place in the app that reports a
   * refusal and leaves a dead token in `sessionStorage`.
   */
  const endSessionIfExpired = (error: unknown) => {
    // Branch on the code and never on the status: a 401 is also
    // `AUTH_CREDENTIALS_INVALID` (a sign-in that was refused, where there is
    // no session to end) and `AUTH_TERMINAL_LEASE_EXPIRED` (a device, not a
    // person). Only this code means the session this app holds is over.
    //
    // Checked against the server rather than inferred, because the reasonable
    // guess is wrong: `SessionService.resolve` answers `null` for *every* way
    // a token can fail — unknown, ended, past its idle window, past its
    // ceiling, or belonging to a deactivated user — and all of them are
    // answered as `AUTH_SESSION_EXPIRED`, deliberately, so that a refusal
    // cannot tell an attacker whether a token it holds was ever real. There
    // is no dead-session case hiding under `AUTH_CREDENTIALS_INVALID` to
    // catch here, and adding it would end a live session on a mistyped
    // password at the sign-in form.
    if (error instanceof ApiError && error.code === 'AUTH_SESSION_EXPIRED')
      session.clear('expired');
  };

  const request: ApiClient['request'] = async (operation, ...args) => {
    try {
      return await client.request(operation, ...args);
    } catch (error) {
      endSessionIfExpired(error);

      throw error;
    }
  };

  const download: ApiClient['download'] = async (operation, ...args) => {
    try {
      return await client.download(operation, ...args);
    } catch (error) {
      endSessionIfExpired(error);

      throw error;
    }
  };

  return { request, download };
}

/**
 * Undefined until a provider supplies one. `useApiClient` refuses rather than
 * quietly constructing a second client with different configuration.
 */
export const ApiClientContext = createContext<ApiClient | undefined>(undefined);

export function useApiClient(): ApiClient {
  const client = useContext(ApiClientContext);

  if (!client) {
    throw new Error('No ApiClientContext above this component. Provide one at the app root.');
  }

  return client;
}
